import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext'; 
import { useAuth } from '../context/AuthContext';
import { useFilter } from '../context/FilterContext'; // Import useFilter
import { FaHeart, FaShoppingCart, FaSearch, FaUserCircle, FaBars, FaTimes } from 'react-icons/fa'; 

const Navbar = () => {
    const { cart, categories } = useStore(); // Get categories from StoreContext
    const { currentUser, logout } = useAuth();
    const { 
        searchTerm, 
        setSearchTerm, 
        selectedCategory, 
        setSelectedCategory 
    } = useFilter(); // Use FilterContext
    const navigate = useNavigate();
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const ADMIN_EMAILS = ["newton108000@gmail.com", "lumiotec.orders@gmail.com", "okumuwan@gmail.com", "cynthiamusonye1@gmail.com"];

    const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);
    const wishlistCount = 0; 

    // The form submission is prevented, filtering is now live.
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        // Optional: navigate to a search-focused page if desired, but for now we do nothing.
    };

    const handleLogout = async () => {
        try {
            await logout(); 
            navigate('/login');
        } catch (error) {
            console.error('Failed to log out:', error);
            alert("Failed to log out!");
        }
    };

    return (
        <header className="bg-gray-800 text-white shadow-lg sticky top-0 z-50">
            {/* Top Bar */}
            <div className="bg-gray-900 py-2 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <Link to="/" className="flex-shrink-0">
                        <img src="/lumio_logo.png" alt="LumioTec" className="h-8 w-auto filter brightness-0 invert" />
                    </Link>

                    {/* Desktop Search Bar */}
                    <form onSubmit={handleSearchSubmit} className="hidden md:flex flex-grow max-w-xl mx-4">
                        <select 
                            value={selectedCategory} 
                            onChange={(e) => setSelectedCategory(e.target.value)} 
                            className="bg-gray-200 text-gray-800 text-sm font-semibold px-3 rounded-l-md border-r border-gray-300 focus:outline-none"
                        >
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                        <input 
                            type="text" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            placeholder="Search the store..." 
                            className="flex-grow p-2 text-gray-800 focus:outline-none"
                        />
                        <button type="submit" className="bg-yellow-400 text-blue-900 px-4 rounded-r-md hover:bg-yellow-300 transition-colors">
                            <FaSearch />
                        </button>
                    </form>

                    <div className="hidden md:flex items-center space-x-6">
                        {currentUser ? (
                            <div className="relative group">
                                <button className="flex items-center space-x-2">
                                    <FaUserCircle size={24} />
                                    <span className="text-sm font-medium">Hi, {currentUser.email.split('@')[0]}</span>
                                </button>
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 hidden group-hover:block">
                                    <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Profile</Link>
                                    {ADMIN_EMAILS.includes(currentUser.email) && (
                                        <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Admin Dashboard</Link>
                                    )}
                                    <button onClick={handleLogout} className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Logout</button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex space-x-4">
                                <Link to="/login" className="text-sm font-semibold hover:text-yellow-400 transition-colors">Sign In</Link>
                                <Link to="/signup" className="text-sm font-semibold bg-yellow-400 text-blue-900 px-4 py-2 rounded-md hover:bg-yellow-300 transition-colors">Register</Link>
                            </div>
                        )}
                        <Link to="/wishlist" className="relative hover:text-yellow-400 transition-colors">
                            <FaHeart size={24} />
                            {wishlistCount > 0 && <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">{wishlistCount}</span>}
                        </Link>
                        <Link to="/cart" className="relative flex items-end space-x-1 hover:text-yellow-400 transition-colors">
                            <FaShoppingCart size={32} />
                            <span className="text-lg font-bold text-yellow-400">{cartItemCount}</span>
                            <span className="text-sm font-medium pb-1">Cart</span>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden flex items-center">
                        <button onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}>
                            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden bg-gray-800 p-4">
                    <form onSubmit={handleSearchSubmit} className="flex mb-4">
                        <input 
                            type="text" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            placeholder="Search..." 
                            className="flex-grow p-2 text-gray-800 rounded-l-md focus:outline-none"
                        />
                        <button type="submit" className="bg-yellow-400 text-blue-900 px-4 rounded-r-md"><FaSearch /></button>
                    </form>
                    <nav className="flex flex-col space-y-2">
                        {currentUser ? (
                            <>
                                <Link to="/profile" className="hover:text-yellow-400">Profile</Link>
                                <button onClick={handleLogout} className="text-left hover:text-yellow-400">Logout</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="hover:text-yellow-400">Sign In</Link>
                                <Link to="/signup" className="hover:text-yellow-400">Register</Link>
                            </>
                        )}
                        <Link to="/wishlist" className="hover:text-yellow-400">Wishlist</Link>
                        <Link to="/cart" className="hover:text-yellow-400">Cart ({cartItemCount})</Link>
                        <hr className="my-2 border-gray-700"/>
                        <Link to="/" className="hover:text-yellow-400">Shop</Link>
                        <Link to="/about" className="hover:text-yellow-400">About Us</Link>
                        <Link to="/contact" className="hover:text-yellow-400">Contact Us</Link>
                    </nav>
                </div>
            )}

            {/* Secondary Nav Bar */}
            <nav className="hidden md:block bg-gray-700 py-2 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto flex items-center space-x-6">
                    {/* The category dropdown in the main search bar now handles this */}
                    <Link to="/" className="text-sm font-semibold hover:text-yellow-400 transition-colors">Shop</Link>
                    <Link to="/about" className="text-sm font-semibold hover:text-yellow-400 transition-colors">About Us</Link>
                    <Link to="/contact" className="text-sm font-semibold hover:text-yellow-400 transition-colors">Contact Us</Link>
                </div>
            </nav>
        </header>
    );
};

export default Navbar;